import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DOCUMENT } from '@angular/common';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, take, tap } from 'rxjs/operators';

import { flattenDestinations } from '@fcom/destination-search/utils/flatten';
import { finShare } from '@fcom/rx';
import { MediaQueryService } from '@fcom/common';
import { ButtonComponent, ButtonSize, ButtonTheme, ChipTheme, IconPosition } from '@fcom/ui-components';
import {
  isBlank,
  isNotBlank,
  isNotEmpty,
  unsubscribe,
  KeyCodes,
  getKeyCodeFromEvent,
  stopPropagation,
} from '@fcom/core/utils';
import {
  DestinationSearchItem,
  DestinationSearchItemFilterSelection,
  DestinationSearchItemType,
  FilterAction,
} from '@fcom/destination-search/interfaces';

const MODAL_OPEN_CLASS_NAME = 'modal-open';

const FILTERS_ORDER = ['Finland', 'Europe', 'Middle East', 'Americas', 'Asia-Pacific', 'Africa'];

@Component({
  selector: 'fin-destination-search-bar',
  templateUrl: 'destination-search-bar.component.html',
  styleUrls: ['destination-search-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DestinationSearchBarComponent implements OnInit, OnDestroy {
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly DESTINATION_TYPE = DestinationSearchItemType;
  readonly IconLibrary = IconLibrary;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ChipTheme = ChipTheme;

  @Input()
  selectedDestinations: DestinationSearchItem[] = [];

  /**
   * Destinations that the user can choose from, also contains countries and continents
   */
  @Input()
  allDestinations$: Observable<DestinationSearchItem[]>;

  @Input()
  closeOptionsList$: Observable<void>;

  /**
   * Emits this.selectedDestinationIds
   */
  @Output()
  valueChange = new EventEmitter<DestinationSearchItemFilterSelection>();

  @Output() inputFocusState = new EventEmitter<boolean>();

  @Output() optionsListVisibilityChanged = new EventEmitter<boolean>();

  @ViewChild('inputField')
  inputField: ElementRef<HTMLInputElement>;

  @ViewChild('inputFieldButton', { static: false })
  inputFieldButton: ElementRef;

  @ViewChild('showResultsButton', { static: false })
  showResultsButton: ButtonComponent;

  @ViewChild('tagList', { static: false })
  tagList: ElementRef;

  /**
   * Destinations that match the given query
   */
  matchingDestinations$: BehaviorSubject<DestinationSearchItem[]> = new BehaviorSubject(undefined);

  /**
   * Destination options list visibility
   */
  optionsListVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  continents$: Observable<DestinationSearchItem[]>;
  selectedContinent$: BehaviorSubject<DestinationSearchItem> = new BehaviorSubject(undefined);
  isStartTyping$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isVisible$: Subject<boolean> = new Subject();

  destinationInput = new UntypedFormControl();
  isMobile$: Observable<boolean>;

  private subscriptions: Subscription = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private mediaQueryService: MediaQueryService,
    private readonly renderer: Renderer2,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.continents$ = this.allDestinations$.pipe(
      take(1),
      map((allDestinations: DestinationSearchItem[]) => this.mainFilters(allDestinations)),
      map((allDestinations: DestinationSearchItem[]) => this.recursiveMapDestinationCount(allDestinations)),
      distinctUntilChanged(),
      finShare()
    );

    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');

    this.subscriptions.add(
      combineLatest([this.isMobile$, this.isVisible$])
        .pipe(
          map(([isMobile, isVisible]: [boolean, boolean]) => isMobile && isVisible),
          distinctUntilChanged()
        )
        .subscribe((shouldSetOverlay: boolean) => {
          if (shouldSetOverlay) {
            this.renderer.addClass(this.document.body, MODAL_OPEN_CLASS_NAME);
          } else {
            this.renderer.removeClass(this.document.body, MODAL_OPEN_CLASS_NAME);
          }
        })
    );

    // search by writing
    this.subscriptions.add(
      combineLatest([
        this.destinationInput.valueChanges.pipe(
          debounceTime(200),
          distinctUntilChanged(),
          startWith(''),
          tap((searchString: string) => this.isStartTyping$.next(searchString?.length > 0))
        ),
        this.continents$.pipe(filter(Boolean), distinctUntilChanged()),
      ])
        .pipe(
          tap(() => this.matchingDestinations$.next(undefined)),
          filter(([query]) => isNotBlank(query)),
          map(([query, destinations]: [string, DestinationSearchItem[]]) => {
            const updated = this.mapQueryMatches(destinations, query);
            return flattenDestinations(updated)
              .filter((d: DestinationSearchItem) => d.matchesQuery)
              .filter(
                (tag, index, array) =>
                  array.findIndex((t) => `${t.title}-${t.countryCode}` === `${tag.title}-${tag.countryCode}`) === index
              );
          })
        )
        .subscribe((destinations: DestinationSearchItem[]) => {
          this.updateDestinationOptionsList(destinations);
        })
    );

    // get destination list by continent
    this.subscriptions.add(
      combineLatest([this.selectedContinent$, this.continents$.pipe(filter(Boolean), distinctUntilChanged())])
        .pipe(
          map(([continent, destinations]: [DestinationSearchItem, DestinationSearchItem[]]) =>
            destinations.filter((destination: DestinationSearchItem) => destination?.id === continent?.id)
          )
        )
        .subscribe((destinations: DestinationSearchItem[]) => {
          /* TODO: timeout is added to defer heavy operations to the next tick
           * We want to close the modal immediately to give the user feedback and improve SEO score
           * Slow update of destinations should still be investigated and fixed
           */
          setTimeout(() => {
            this.updateDestinationOptionsList(destinations);
          }, 0);
        })
    );

    this.subscriptions.add(
      this.optionsListVisible$.pipe(distinctUntilChanged()).subscribe((isVisible: boolean) => {
        this.optionsListVisibilityChanged.emit(isVisible);
      })
    );

    this.subscriptions.add(
      this.closeOptionsList$.subscribe(() => {
        this.closeOptionsList();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  get inputHasFocus(): boolean {
    return this.document.activeElement === this.inputField?.nativeElement;
  }

  openModal(event: Event): void {
    stopPropagation(event);

    this.isVisible$.next(true);
    this.openOptionsList(true);
    setTimeout(() => {
      // wait a bit before the input field is rendered
      this.inputField?.nativeElement.focus();
    }, 100);
  }

  resetInputValue(isMobile: boolean): void {
    if (this.inputField && this.inputField?.nativeElement) {
      this.inputField.nativeElement.value = '';
      this.isStartTyping$.next(false);
      this.openOptionsList(isMobile);
      this.inputField.nativeElement.focus();
    }
  }

  handleComponentFocusOut(event: FocusEvent): void {
    event.preventDefault();

    const el: HTMLElement = this.elementRef?.nativeElement;
    const target = event.relatedTarget as Element;
    if (!!el && !el.contains(event.target as HTMLElement) && !target?.className.includes('tag-button')) {
      event.stopPropagation();
      this.closeOptionsList();
    }
  }

  onClickInputArea(event: MouseEvent, isMobile: boolean): void {
    // Distinguish between button area and input field clicks
    if (this.inputFieldButton?.nativeElement.contains(event.target)) {
      if (!this.optionsListVisible$.getValue()) {
        this.openOptionsList(isMobile);
      } else if (isMobile) {
        this.resetInputValue(isMobile);
      } else {
        this.closeOptionsList();
      }
    } else {
      this.openOptionsList(isMobile);
    }
  }

  openOptionsList(isMobile = false): void {
    this.inputFocusState.emit(!isMobile);
    this.optionsListVisible$.next(true);
    this.selectedContinent$.next(undefined);
    this.matchingDestinations$.next(undefined);
  }

  closeOptionsList(): void {
    this.inputFocusState.emit(false);
    this.destinationInput.reset();
    this.optionsListVisible$.next(false);
    this.isVisible$.next(false);
  }

  removeTag(destination: DestinationSearchItem): void {
    const showButton: HTMLElement = this.showResultsButton?.buttonElement?.nativeElement;
    const tags: NodeListOf<HTMLElement> = this.tagList?.nativeElement.querySelectorAll('fcom-chip');
    const remainingTags = Array.from(tags).filter((c: HTMLElement) => c.id !== destination.id);
    const lastTag = remainingTags[remainingTags?.length - 1];

    if (!lastTag) {
      showButton.focus();
    }

    this.valueChange.emit({
      action: FilterAction.REMOVE,
      destination,
      selectedDestinations: this.selectedDestinations,
    });
  }

  removeAll(): void {
    this.closeOptionsList();
    /* TODO: timeout is added to defer heavy operations to the next tick
     * We want to close the modal immediately to give the user feedback and improve SEO score
     * Slow update of destinations should still be investigated and fixed
     */
    setTimeout(() => {
      this.valueChange.emit({
        action: FilterAction.REMOVE_ALL,
        destination: undefined,
        selectedDestinations: this.selectedDestinations,
      });
    }, 0);
  }

  toggleDestinationSelection(destination: DestinationSearchItem, country: DestinationSearchItem = undefined): void {
    const isSelected = Boolean(this.selectedDestinations?.find((selected) => selected.id === destination.id));

    const countrySelectedDestinations = this.destinationIsSelected(destination)
      ? [...(country && country?.items ? [...country.items.filter((dest) => dest.id !== destination.id)] : [])]
      : [
          ...this.selectedDestinations.filter(
            (selection) =>
              selection.type === DestinationSearchItemType.DESTINATION && selection.countryCode === country?.countryCode
          ),
          ...(!isSelected && destination.type === DestinationSearchItemType.DESTINATION ? [destination] : []),
        ];
    const shouldSelectAll = country?.items?.every((item) => countrySelectedDestinations.includes(item));

    if (
      (country && !isSelected && shouldSelectAll) ||
      (!isSelected && destination.type === DestinationSearchItemType.COUNTRY)
    ) {
      // special case 1.
      // when all destination selected from country one by one
      // remove the destinations and select country

      // special case 2.
      // when all destinations selected by button
      // remove all destinations from country and select country
      setTimeout(() => {
        this.valueChange.emit({
          action: FilterAction.CUSTOM,
          destination: undefined,
          selectedDestinations: [
            ...this.selectedDestinations.filter((selected) => selected.countryCode !== destination.countryCode),
            country ? { ...country } : { ...destination },
          ],
        });
      }, 100);
    } else if (
      country &&
      !isSelected &&
      !shouldSelectAll &&
      destination.type === DestinationSearchItemType.DESTINATION
    ) {
      // special case 3.
      // when all destinations selected by button and then unselect one destination
      // remove country selection and add all destinations from country without the deselected one

      const filteredDestinationByCountry = this.selectedDestinations.filter(
        (selected) =>
          selected.type === DestinationSearchItemType.COUNTRY && selected.countryCode !== destination.countryCode
      );

      // special case 4.
      // when some destinations selected from different country
      // should merge it with selectors from different country or with another destination
      const filteredDestinationByDestination = this.selectedDestinations.filter(
        (selected) =>
          selected.type === DestinationSearchItemType.DESTINATION && selected.countryCode !== destination.countryCode
      );

      setTimeout(() => {
        this.valueChange.emit({
          action: FilterAction.CUSTOM,
          destination: undefined,
          selectedDestinations: [
            ...filteredDestinationByCountry,
            ...filteredDestinationByDestination,
            ...countrySelectedDestinations,
          ],
        });
      }, 100);
    } else {
      setTimeout(() => {
        // add/remove single destination
        this.valueChange.emit({
          action: isSelected ? FilterAction.REMOVE : FilterAction.ADD,
          destination,
          selectedDestinations: this.selectedDestinations,
        });
      }, 100);
    }
  }

  toggleDestinationByKeyboard(
    $event: KeyboardEvent,
    destination: DestinationSearchItem,
    country: DestinationSearchItem = undefined
  ): void {
    setTimeout(() => {
      if (getKeyCodeFromEvent($event) === KeyCodes.SPACE) {
        this.toggleDestinationSelection(destination, country);
      }
    }, 100);
  }

  destinationIsSelected(destination: DestinationSearchItem): boolean {
    return Boolean(
      this.selectedDestinations?.find(
        (d) =>
          d.id === destination.id ||
          (d.type === DestinationSearchItemType.COUNTRY && d.countryCode === destination.countryCode)
      )
    );
  }

  trackByFn(_index: number, item: DestinationSearchItem): string {
    return item.id;
  }

  /**
   * test if destination's attributes contain words that start with the given query
   */
  private destinationMatchesQuery(destination: DestinationSearchItem, query: string): boolean {
    // title that we test if it contains any of the queries.
    // Mark each word start with a space so that search “sin” doesn’t match “Helsinki”,
    // but we can match on every word start, including “Palma De Mallorca’s” Mallorca
    const t =
      ` ${destination.title} ${destination.normalizedTitle} ${destination.titleMaster} ${destination.normalizedTitleMaster}`.toLowerCase();

    return t.includes(` ${query?.toLowerCase()}`);
  }

  private recursiveMatchCheck(destination: DestinationSearchItem, query: string): boolean {
    const selfMatch = this.destinationMatchesQuery(destination, query);
    if (isNotEmpty(destination.items)) {
      destination.items.some((child) => this.recursiveMatchCheck(child, query));
    }

    return selfMatch;
  }

  private mapQueryMatches(destinations: DestinationSearchItem[], query: string): DestinationSearchItem[] {
    return destinations?.map((destination) => ({
      ...destination,
      items: this.mapQueryMatches(destination.items, query),
      matchesQuery: isBlank(query) || this.recursiveMatchCheck(destination, query),
    }));
  }

  private updateDestinationOptionsList(destinations: DestinationSearchItem[]): void {
    const query = this.destinationInput.value;
    this.matchingDestinations$.next(destinations);

    if (isNotBlank(query) && !this.optionsListVisible$.getValue()) {
      this.openOptionsList();
    }
  }

  private recursiveMapDestinationCount(destinations: DestinationSearchItem[]) {
    return destinations?.map((destination: DestinationSearchItem) => {
      if (isNotEmpty(destination.items)) {
        return {
          ...destination,
          items: [...this.recursiveMapDestinationCount(destination.items).sort(this.sortByAlphabetical)],
          count: this.mapDestinations(destination.items),
        };
      } else {
        return {
          ...destination,
        };
      }
    });
  }

  private mapDestinations(destinations: DestinationSearchItem[]): number {
    return destinations
      .map((value) => (isNotEmpty(value.items) ? this.mapDestinations(value.items) : 1))
      .reduce((acc, value) => acc + value, 0);
  }

  private sortByAlphabetical(a: DestinationSearchItem, b: DestinationSearchItem) {
    return a.title > b.title ? 1 : -1;
  }

  private mainFilters(destinations: DestinationSearchItem[]): DestinationSearchItem[] {
    const result: DestinationSearchItem[] = [];

    for (const destination of destinations) {
      if (destination.type === DestinationSearchItemType.CONTINENT && destination.titleMaster === 'Europe') {
        const finland = destination.items.find((item) => item.titleMaster === 'Finland');
        if (finland) {
          result.unshift(finland);
        }
        result.push({ ...destination, items: [...destination.items.filter((item) => item.titleMaster !== 'Finland')] });
      } else if (isNotEmpty(destination?.items)) {
        result.push(destination);
      }
    }

    return result.sort((a, b) => FILTERS_ORDER.indexOf(a.titleMaster) - FILTERS_ORDER.indexOf(b.titleMaster));
  }
}
